import { EN_CONTENTS_TYPE, EN_CONTENTS_TYPE_CODE } from '@@sapporo/constant/enum/EN_CONTENTS_TYPE';
import { EN_ROOM_PRODUCT_STATE_TYPE } from '@@sapporo/constant/enum/place/EN_ROOM_PRODUCT_STATE';
import { EN_STAY_SERVICE_TYPE } from '@@sapporo/constant/enum/place/EN_STAY_SERVICE_TYPE';
import { IDesignedString } from '@@sapporo/design/IDesignedString';
import { INotice, IYanoljaCareBanner } from '@@sapporo/local/detail/interface';
import { IRelation } from '@@sapporo/local/detail/interface/IRelation';
import {
  IRoomProductDiscountRate,
  IRoomProductInventory,
  IRoomProductPointEarnPolicy,
} from '@@sapporo/local/detail/interface/IRoomProduct';
import { EN_HOTELGRADE_AUTH_TYPE } from '@@sapporo/local/search/enum/EN_PF_HOTELGRADE_AUTH_TYPE';
import { IGlobalPhoto } from '@@sapporo/photo/interface';
import { IPDPBanner } from '@@sapporo/banner/interface/IPDPBanner';

import { IModifiers } from '../../../models/Search/interfaces/ISearchListPlaceItem';
import { IReviewSerialized } from '../../Review/interfaces/IReviewsSerialized';
import { IRoom } from '../../Room/interfaces/IRoom';

export { EN_AFFILIATION_SCALE_TYPE } from '@@sapporo/constant/enum/advertise/EN_AFFILIATION_SCALE';
export { EN_CONTENTS_TYPE_CODE, EN_STAY_SERVICE_TYPE };

export type TContentsType = EN_CONTENTS_TYPE_CODE | EN_CONTENTS_TYPE | 'LEISURE';

export type THotelGradeType = EN_HOTELGRADE_AUTH_TYPE;

/**
 * 숙소상세 페이지를 그리기 위해 필요한 속성들
 * 대부분은 service-data-model에 정의되어 있는 IGlobalPlace와 동일하지만 평탄화할 수 있는 자료는 평탄화
 */
export interface IPlaceDetailSerialized
  extends TPlaceTitle,
    TPlaceBottomTerm,
    TPlaceAddress,
    TPlacePhotos,
    TBusinessInfo,
    TEmphasis,
    TPathInfo,
    TSpecialCharge,
    TPickupInfo,
    TParkingInfo,
    TReservation,
    TFacilities,
    TFacilityNotice,
    TNotices,
    TUsingRules,
    TRoomList,
    TRelation,
    TPlacePhotoInfo,
    TPlaceBanners {
  /** 숙소 고유번호 */
  no: number;

  /** 컨텐츠 타입, 나중에 쓸 일이 있어보인다 */
  contentsType: TContentsType;

  /** 호텔인 경우 지정되는 등급 */
  grade: string | null;

  /** 등급정보 */
  gradeInfo: {
    /** 등급명(PF > className): 1성급, 2성급 등 ... */
    title: string | null;
    /** 인증 정보
     * 해당 필드 null 여부로 아이콘 및 설명글에 대한 노출 여부가 결정된다.
     * EN_HOTELGRADE_AUTH_TYPE.HN 은 공식인증이 아닌 호텔나우 자체 인증이므로 아이콘 노출 대상이 아니므로 null로 내려가게 한다.
     */
    authenticate: {
      /** 인증 기관 구분 */
      type: EN_HOTELGRADE_AUTH_TYPE;
      /** 인증 기관명 안내 문구(ex: 한국관광공사에서 인증한 호텔등급입니다.) */
      agencyDesc: string;
      /** 등급 결정일 안내 문구(PF > classDate, ex: 등급결정일 2017.07.01) */
      updatedAtDesc: string;
    } | null;
  };

  /** 숙소 설명, 업체 소개 */
  desc: string | null;
  /** 찜하기(가볼래요) 여부 */
  favorite: boolean;

  /** 위도 */
  lat: number;
  /** 경도 */
  lng: number;
  /** 일단 대표 주소만 뽑아서 표기 */
  address: string;
  /** 02xxxxxxxx 로 대표번호만 가져옴 */
  contact: string;
  review: IReviewSerialized | null;
  eventBadges: IDesignedString[];
  hideWeb: boolean;
}

/** 하단 고지사항 */
export type TPlaceBottomTerm = {
  bottomTerm: string;
  /** 어느 채널을 통해 제공되었나, CS전화번호를 제공하는 필드가 있지만 안쓰므로 이름만 가져와서 뿌려줌 */
  channel: string | null;
};

export type TPlaceEventBadges = {
  eventBadges: IDesignedString[] | null;
};

export type TPlaceBanners = {
  benefitBanner?: IPDPBanner | null;
  yanoljaCareBanner?: IYanoljaCareBanner | null;
  contentsBanner?: IPDPBanner | null;
};

/** 숙소 제목 */
export type TPlaceTitle = {
  title: string;
};

/** 숙소 주소, 일단 대표 주소만 가져옴 */
export type TPlaceAddress = {
  address: string | null;
};

/** 숙소 포토정보 */
export type TPlacePhotoInfo = {
  representative: IGlobalPhoto;
  total: number;
};

/** URL들의 모음으로 한다. 반드시 배열의 첫 번째 요소는 대표 사진으로 지정한다. */
export type TPlacePhotos = {
  photos: TPlacePhoto[];
};
export type TPlacePhoto = {
  uri: string | null;
  title: string | null;
};

/** 원본 데이터에는 모든 강조태그가 다 나오고 available로 활성화 여부를 표시한다. 가능한 title만 가져옴 */
export type TEmphasis = {
  emphasis: string[];
};

/** 비지니스와 관련된 추가정보 */
export type TBusinessInfo = {
  /** 할인 문구. 정보가 굉장히 동적으로 내려오기 때문에 상황에 따른 조합을 하기 위해 배열을 내려받는다 */
  byOrderOfArriavalCoupon: IDesignedString[] | null;
  /** Q&A 출력 여부 */
  showQnA: boolean;
};

export type TPathInfo = {
  /** 위치 간략 정보. 원본 데이터는 배열 형태로 내려오지만 하나로 연결해놓는 것이 나아보인다 */
  pathSummary: string;
  /** 길찾기 설명인데 없을 수 있음 */
  pathGuides: string[] | null;
};

/**
 * 펜션인 경우 픽업에 관한 설명이 있다. null일 경우 빈 문자열로 처리
 * 펜션이 아닌 숙소의 경우 이 속성을 아예 가져다 쓰지 않도록 구성한다
 */
export type TPickupInfo = {
  pickup: {
    title: string;
    desc: string[];
  };
};

/** 성수기 안내를 표시하기 위한 자료. 없으면 null로 나타남 */
export type TSpecialCharge = {
  specialCharge: TSpecialChargeItem[] | null;
};
export type TSpecialChargeItem = {
  title: string;
  desc: string;
  chargeDuration: string; // 조건에 따라 시작 ~ 종료 기간을 표시한 string
};

/** 주차가 가능하다면 fee, valet 속성을 상황에 따라 표시하겠지만 애초에 주차가 isHaveSpace가 false면 표시할 필요가 없음 */
export type TParkingInfo = {
  parking: {
    isHaveSpace: boolean;
    available: boolean;
    fee?: TParkingFee;
    valet?: TValetFee;
  };
};

export type TParkingFee = {
  hour: string;
  minute: string;
  day: string;
};

export type TValetFee = {
  available: boolean;
  fee?: string;
};

export type TReservation = {
  reservation: {
    available: boolean;
    /** 미리예약이 몇일까지 가능한가? 주로 0, 7, 30, 60 */
    maxOutRange: number;
    /** 9박 10일 이런식으로 내려온다는데, 처음에는 뒤의 일수만 가져오려다가 혹시몰라서 다 가져옴 */
    duration: [number, number];
  };
};

/** 편의시설 및 테마에 대한 설명 */
export type TFacilities = {
  facilities: TFacility[];
};
export type TFacility = {
  title: string;
  fee?: number;
  icon: {
    matched: string;
    available: string;
    unavailable: string;
  };
};

export type TFacilityNotice = {
  facilityNotice: string[];
};

/** 이용안내를 제외한 공지사항 */
export type TNotices = {
  notices: TNotice[];
};
export type TNotice = Pick<INotice, 'title' | 'items' | 'type'>;

/** 이용안내 */
export type TUsingRules = {
  usingRules: {
    title: string;
    items: string[];
  }[];
};

export type TRoomList = {
  rooms: TRoomListItem[];
};
export type TRoomListItem = {
  no: number;
  title: string;
  desc: string | null;
  onlyAdvertise: boolean;
  maintenance: boolean;
  reserveBadgeStatus: EN_RESERVE_BADGE_STATUS;
  smoking: boolean | null;
  coupon: IRoom['coupon'];
  /** 기준인원 / 최대인원 - 나이대에 따라 추가요금이 발생하는 등의 로직은 추후 고려 */
  capacity: string | null; // 조건에 따라 맞는 string을 만들어서 내려준다. 없는 경우가 있다.

  /** 대표 사진의 uri */
  photo: IGlobalPhoto['uri'];

  /** 가격정보 */
  prices: TRoomPrice[];
  /** 상품 속성 뱃지 */
  eventBadges?: IDesignedString[] | null;
};
export type TRoomSinglePrice = {
  title: string;
  amount: number;
  price: string;
  labelType: number;
};
export type TRoomPriceGroup = {
  origin: TRoomSinglePrice | null;
  discount: TRoomSinglePrice | null;
  coupon: TRoomSinglePrice | null;
};

export type TRoomPrice = {
  /** 대실, 숙박, 연박 */
  type: EN_STAY_SERVICE_TYPE;

  /** 상품이름: 대실, 숙박, 연박 */
  title: string;

  /** 최대 N시간 / 14:00 입실 등의 문구 */
  time: string;

  /** 가격 - 기준가, 할인가, 쿠폰가 **/
  price: TRoomPriceGroup;

  /** 연박인 경우 기준가를 표시해야 한다. */
  footNote?: string;

  /** 상태 - 예약 마감일 경우 가격노출 하지 않는 조건을 위한 값 */
  state: TState;

  /** 뱃지(핫딜,쿠폰) */
  modifiers: IModifiers;

  /** 할인율 */
  discountRate: IRoomProductDiscountRate | null;

  /** 포인트 적립율 */
  pointEarn: IRoomProductPointEarnPolicy | null;

  /** 남은 객실 수 */
  inventory: IRoomProductInventory | null;
};

/** 방 상태 정보 */
export type TState = {
  desc: string;
  title: string;
  subTitle: string;
  type: EN_ROOM_PRODUCT_STATE_TYPE;
};

/** 할인율 */
export type TDiscountRate = {
  acount: number;
  unit: '%';
};

/** 제휴정보 */
export type TRelation = {
  relation: IRelation & { isAssociation: boolean };
};

/**
 * 예약 상태 표시를 위한 필드
 * @field ON_SALE 예약가능한 객실 - 예약Y + 재고있음
 * @field SOLD_OUT 예약가능하나 마감된 객실 - 예약Y + 재고없음
 * @field WALK_IN 광고만 진행하는 객실 - 예약상태 표시 안함
 */
export enum EN_RESERVE_BADGE_STATUS {
  ON_SALE,
  SOLD_OUT,
  WALK_IN,
}
/**
 * 가격을 표시할 시 예약가인지 현장결제가인지
 * 뱃지 타입에 따라 스타일을 다르게 표현하기 위해 타입을 지정
 */
export enum EN_PRICE_LABEL_TYPE {
  RESERVE_PRICE,
  WALK_IN_PRICE,
}
